// ============================================================
// MyGene Design System — Color Groups
// Each accent color maps to a full semantic palette so every
// element in the UI (success, warning, danger, info, tags)
// harmonises with the chosen brand colour.
// ============================================================

export interface ColorGroup {
	// Core brand shades
	primary: string;
	primaryHover: string;
	primaryActive: string;
	primaryBg: string;          // very light tint — used for active nav bg, tag bg
	primaryBgHover: string;
	primaryBorder: string;      // border when indicating "selected"
	primaryBorderHover: string;

	// Semantic colours adjusted to complement the primary
	success: string;
	successBg: string;
	successBorder: string;

	warning: string;
	warningBg: string;
	warningBorder: string;

	danger: string;
	dangerBg: string;
	dangerBorder: string;

	info: string;
	infoBg: string;
	infoBorder: string;

	// Tag palette — five harmonious tag colours
	tags: {
		blue: string;
		green: string;
		orange: string;
		red: string;
		purple: string;
		cyan: string;
		gold: string;
		pink: string;
	};
}

// ────────────────────────────────────────────────────────────
// Helper: hex → "r, g, b" string for CSS rgba()
// ────────────────────────────────────────────────────────────
export function hexToRgb(hex: string): string {
	const h = hex.replace('#', '');
	const bigint = parseInt(h.length === 3
		? h.split('').map(c => c + c).join('')
		: h, 16);
	return `${(bigint >> 16) & 255}, ${(bigint >> 8) & 255}, ${bigint & 255}`;
}

// ────────────────────────────────────────────────────────────
// Shared tag palette (consistent across all themes)
// ────────────────────────────────────────────────────────────
const STANDARD_TAGS: ColorGroup['tags'] = {
	blue: '#1890ff',
	green: '#52c41a',
	orange: '#fa8c16',
	red: '#ff4d4f',
	purple: '#722ed1',
	cyan: '#13c2c2',
	gold: '#faad14',
	pink: '#eb2f96',
};

// ────────────────────────────────────────────────────────────
// COLOUR GROUP REGISTRY
// ────────────────────────────────────────────────────────────

export const COLOR_GROUPS: Record<string, ColorGroup> = {

	// ── BLUES ─────────────────────────────────────────────────

	blue: {
		primary: '#1890ff',
		primaryHover: '#40a9ff',
		primaryActive: '#096dd9',
		primaryBg: 'rgba(24, 144, 255, 0.08)',
		primaryBgHover: 'rgba(24, 144, 255, 0.12)',
		primaryBorder: 'rgba(24, 144, 255, 0.35)',
		primaryBorderHover: 'rgba(24, 144, 255, 0.6)',
		success: '#52c41a', successBg: 'rgba(82,196,26,0.08)', successBorder: 'rgba(82,196,26,0.35)',
		warning: '#faad14', warningBg: 'rgba(250,173,20,0.08)', warningBorder: 'rgba(250,173,20,0.35)',
		danger: '#ff4d4f', dangerBg: 'rgba(255,77,79,0.08)', dangerBorder: 'rgba(255,77,79,0.35)',
		info: '#1890ff', infoBg: 'rgba(24,144,255,0.08)', infoBorder: 'rgba(24,144,255,0.35)',
		tags: STANDARD_TAGS,
	},

	navyBlue: {
		primary: '#003a8c',
		primaryHover: '#1d58c0',
		primaryActive: '#002766',
		primaryBg: 'rgba(0, 58, 140, 0.08)',
		primaryBgHover: 'rgba(0, 58, 140, 0.14)',
		primaryBorder: 'rgba(0, 58, 140, 0.35)',
		primaryBorderHover: 'rgba(0, 58, 140, 0.6)',
		success: '#52c41a', successBg: 'rgba(82,196,26,0.08)', successBorder: 'rgba(82,196,26,0.35)',
		warning: '#faad14', warningBg: 'rgba(250,173,20,0.08)', warningBorder: 'rgba(250,173,20,0.35)',
		danger: '#ff4d4f', dangerBg: 'rgba(255,77,79,0.08)', dangerBorder: 'rgba(255,77,79,0.35)',
		info: '#1d58c0', infoBg: 'rgba(29,88,192,0.08)', infoBorder: 'rgba(29,88,192,0.35)',
		tags: STANDARD_TAGS,
	},

	skyBlue: {
		primary: '#40a9ff',
		primaryHover: '#69c0ff',
		primaryActive: '#1890ff',
		primaryBg: 'rgba(64,169,255,0.08)',
		primaryBgHover: 'rgba(64,169,255,0.14)',
		primaryBorder: 'rgba(64,169,255,0.35)',
		primaryBorderHover: 'rgba(64,169,255,0.6)',
		success: '#52c41a', successBg: 'rgba(82,196,26,0.08)', successBorder: 'rgba(82,196,26,0.35)',
		warning: '#faad14', warningBg: 'rgba(250,173,20,0.08)', warningBorder: 'rgba(250,173,20,0.35)',
		danger: '#ff4d4f', dangerBg: 'rgba(255,77,79,0.08)', dangerBorder: 'rgba(255,77,79,0.35)',
		info: '#40a9ff', infoBg: 'rgba(64,169,255,0.08)', infoBorder: 'rgba(64,169,255,0.35)',
		tags: STANDARD_TAGS,
	},

	oceanBlue: {
		primary: '#006d75',
		primaryHover: '#08979c',
		primaryActive: '#00474f',
		primaryBg: 'rgba(0,109,117,0.08)',
		primaryBgHover: 'rgba(0,109,117,0.14)',
		primaryBorder: 'rgba(0,109,117,0.35)',
		primaryBorderHover: 'rgba(0,109,117,0.6)',
		success: '#52c41a', successBg: 'rgba(82,196,26,0.08)', successBorder: 'rgba(82,196,26,0.35)',
		warning: '#faad14', warningBg: 'rgba(250,173,20,0.08)', warningBorder: 'rgba(250,173,20,0.35)',
		danger: '#ff4d4f', dangerBg: 'rgba(255,77,79,0.08)', dangerBorder: 'rgba(255,77,79,0.35)',
		info: '#006d75', infoBg: 'rgba(0,109,117,0.08)', infoBorder: 'rgba(0,109,117,0.35)',
		tags: STANDARD_TAGS,
	},

	// ── INDIGO / VIOLET ────────────────────────────────────────

	indigo: {
		primary: '#2f54eb',
		primaryHover: '#597ef7',
		primaryActive: '#1d39c4',
		primaryBg: 'rgba(47,84,235,0.08)',
		primaryBgHover: 'rgba(47,84,235,0.14)',
		primaryBorder: 'rgba(47,84,235,0.35)',
		primaryBorderHover: 'rgba(47,84,235,0.6)',
		success: '#52c41a', successBg: 'rgba(82,196,26,0.08)', successBorder: 'rgba(82,196,26,0.35)',
		warning: '#faad14', warningBg: 'rgba(250,173,20,0.08)', warningBorder: 'rgba(250,173,20,0.35)',
		danger: '#ff4d4f', dangerBg: 'rgba(255,77,79,0.08)', dangerBorder: 'rgba(255,77,79,0.35)',
		info: '#2f54eb', infoBg: 'rgba(47,84,235,0.08)', infoBorder: 'rgba(47,84,235,0.35)',
		tags: STANDARD_TAGS,
	},

	purple: {
		primary: '#722ed1',
		primaryHover: '#9254de',
		primaryActive: '#531dab',
		primaryBg: 'rgba(114,46,209,0.08)',
		primaryBgHover: 'rgba(114,46,209,0.14)',
		primaryBorder: 'rgba(114,46,209,0.35)',
		primaryBorderHover: 'rgba(114,46,209,0.6)',
		// Purple pairs beautifully with teal success
		success: '#13c2c2', successBg: 'rgba(19,194,194,0.08)', successBorder: 'rgba(19,194,194,0.35)',
		warning: '#faad14', warningBg: 'rgba(250,173,20,0.08)', warningBorder: 'rgba(250,173,20,0.35)',
		danger: '#f759ab', dangerBg: 'rgba(247,89,171,0.08)', dangerBorder: 'rgba(247,89,171,0.35)',
		info: '#722ed1', infoBg: 'rgba(114,46,209,0.08)', infoBorder: 'rgba(114,46,209,0.35)',
		tags: { ...STANDARD_TAGS, blue: '#722ed1', purple: '#531dab' },
	},

	violet: {
		primary: '#531dab',
		primaryHover: '#7b3ed6',
		primaryActive: '#391085',
		primaryBg: 'rgba(83,29,171,0.08)',
		primaryBgHover: 'rgba(83,29,171,0.14)',
		primaryBorder: 'rgba(83,29,171,0.35)',
		primaryBorderHover: 'rgba(83,29,171,0.6)',
		success: '#36cfc9', successBg: 'rgba(54,207,201,0.08)', successBorder: 'rgba(54,207,201,0.35)',
		warning: '#faad14', warningBg: 'rgba(250,173,20,0.08)', warningBorder: 'rgba(250,173,20,0.35)',
		danger: '#ff4d4f', dangerBg: 'rgba(255,77,79,0.08)', dangerBorder: 'rgba(255,77,79,0.35)',
		info: '#531dab', infoBg: 'rgba(83,29,171,0.08)', infoBorder: 'rgba(83,29,171,0.35)',
		tags: STANDARD_TAGS,
	},

	lavender: {
		primary: '#b37feb',
		primaryHover: '#d3adf7',
		primaryActive: '#9254de',
		primaryBg: 'rgba(179,127,235,0.08)',
		primaryBgHover: 'rgba(179,127,235,0.14)',
		primaryBorder: 'rgba(179,127,235,0.35)',
		primaryBorderHover: 'rgba(179,127,235,0.6)',
		success: '#52c41a', successBg: 'rgba(82,196,26,0.08)', successBorder: 'rgba(82,196,26,0.35)',
		warning: '#faad14', warningBg: 'rgba(250,173,20,0.08)', warningBorder: 'rgba(250,173,20,0.35)',
		danger: '#ff4d4f', dangerBg: 'rgba(255,77,79,0.08)', dangerBorder: 'rgba(255,77,79,0.35)',
		info: '#b37feb', infoBg: 'rgba(179,127,235,0.08)', infoBorder: 'rgba(179,127,235,0.35)',
		tags: STANDARD_TAGS,
	},

	// ── GREENS ────────────────────────────────────────────────

	green: {
		primary: '#52c41a',
		primaryHover: '#73d13d',
		primaryActive: '#389e0d',
		primaryBg: 'rgba(82,196,26,0.08)',
		primaryBgHover: 'rgba(82,196,26,0.14)',
		primaryBorder: 'rgba(82,196,26,0.35)',
		primaryBorderHover: 'rgba(82,196,26,0.6)',
		success: '#52c41a', successBg: 'rgba(82,196,26,0.08)', successBorder: 'rgba(82,196,26,0.35)',
		warning: '#faad14', warningBg: 'rgba(250,173,20,0.08)', warningBorder: 'rgba(250,173,20,0.35)',
		danger: '#ff4d4f', dangerBg: 'rgba(255,77,79,0.08)', dangerBorder: 'rgba(255,77,79,0.35)',
		info: '#1890ff', infoBg: 'rgba(24,144,255,0.08)', infoBorder: 'rgba(24,144,255,0.35)',
		tags: STANDARD_TAGS,
	},

	forestGreen: {
		primary: '#135200',
		primaryHover: '#237804',
		primaryActive: '#092b00',
		primaryBg: 'rgba(19,82,0,0.08)',
		primaryBgHover: 'rgba(19,82,0,0.14)',
		primaryBorder: 'rgba(19,82,0,0.35)',
		primaryBorderHover: 'rgba(19,82,0,0.6)',
		success: '#237804', successBg: 'rgba(35,120,4,0.08)', successBorder: 'rgba(35,120,4,0.35)',
		warning: '#faad14', warningBg: 'rgba(250,173,20,0.08)', warningBorder: 'rgba(250,173,20,0.35)',
		danger: '#ff4d4f', dangerBg: 'rgba(255,77,79,0.08)', dangerBorder: 'rgba(255,77,79,0.35)',
		info: '#1890ff', infoBg: 'rgba(24,144,255,0.08)', infoBorder: 'rgba(24,144,255,0.35)',
		tags: STANDARD_TAGS,
	},

	mintGreen: {
		primary: '#36cfc9',
		primaryHover: '#5cdbd3',
		primaryActive: '#13a8a8',
		primaryBg: 'rgba(54,207,201,0.08)',
		primaryBgHover: 'rgba(54,207,201,0.14)',
		primaryBorder: 'rgba(54,207,201,0.35)',
		primaryBorderHover: 'rgba(54,207,201,0.6)',
		success: '#52c41a', successBg: 'rgba(82,196,26,0.08)', successBorder: 'rgba(82,196,26,0.35)',
		warning: '#faad14', warningBg: 'rgba(250,173,20,0.08)', warningBorder: 'rgba(250,173,20,0.35)',
		danger: '#ff4d4f', dangerBg: 'rgba(255,77,79,0.08)', dangerBorder: 'rgba(255,77,79,0.35)',
		info: '#36cfc9', infoBg: 'rgba(54,207,201,0.08)', infoBorder: 'rgba(54,207,201,0.35)',
		tags: STANDARD_TAGS,
	},

	sageGreen: {
		primary: '#5f9e6e',
		primaryHover: '#82b991',
		primaryActive: '#3d7a4e',
		primaryBg: 'rgba(95,158,110,0.08)',
		primaryBgHover: 'rgba(95,158,110,0.14)',
		primaryBorder: 'rgba(95,158,110,0.35)',
		primaryBorderHover: 'rgba(95,158,110,0.6)',
		success: '#52c41a', successBg: 'rgba(82,196,26,0.08)', successBorder: 'rgba(82,196,26,0.35)',
		warning: '#faad14', warningBg: 'rgba(250,173,20,0.08)', warningBorder: 'rgba(250,173,20,0.35)',
		danger: '#ff4d4f', dangerBg: 'rgba(255,77,79,0.08)', dangerBorder: 'rgba(255,77,79,0.35)',
		info: '#1890ff', infoBg: 'rgba(24,144,255,0.08)', infoBorder: 'rgba(24,144,255,0.35)',
		tags: STANDARD_TAGS,
	},

	// ── TEALS / CYANS ─────────────────────────────────────────

	teal: {
		primary: '#13c2c2',
		primaryHover: '#36cfc9',
		primaryActive: '#08979c',
		primaryBg: 'rgba(19,194,194,0.08)',
		primaryBgHover: 'rgba(19,194,194,0.14)',
		primaryBorder: 'rgba(19,194,194,0.35)',
		primaryBorderHover: 'rgba(19,194,194,0.6)',
		success: '#52c41a', successBg: 'rgba(82,196,26,0.08)', successBorder: 'rgba(82,196,26,0.35)',
		warning: '#faad14', warningBg: 'rgba(250,173,20,0.08)', warningBorder: 'rgba(250,173,20,0.35)',
		danger: '#ff4d4f', dangerBg: 'rgba(255,77,79,0.08)', dangerBorder: 'rgba(255,77,79,0.35)',
		info: '#13c2c2', infoBg: 'rgba(19,194,194,0.08)', infoBorder: 'rgba(19,194,194,0.35)',
		tags: STANDARD_TAGS,
	},

	cyan: {
		primary: '#0891b2',
		primaryHover: '#0ea5d3',
		primaryActive: '#0e7490',
		primaryBg: 'rgba(8,145,178,0.08)',
		primaryBgHover: 'rgba(8,145,178,0.14)',
		primaryBorder: 'rgba(8,145,178,0.35)',
		primaryBorderHover: 'rgba(8,145,178,0.6)',
		success: '#52c41a', successBg: 'rgba(82,196,26,0.08)', successBorder: 'rgba(82,196,26,0.35)',
		warning: '#faad14', warningBg: 'rgba(250,173,20,0.08)', warningBorder: 'rgba(250,173,20,0.35)',
		danger: '#ff4d4f', dangerBg: 'rgba(255,77,79,0.08)', dangerBorder: 'rgba(255,77,79,0.35)',
		info: '#0891b2', infoBg: 'rgba(8,145,178,0.08)', infoBorder: 'rgba(8,145,178,0.35)',
		tags: STANDARD_TAGS,
	},

	// ── REDS / WARMS ──────────────────────────────────────────

	red: {
		primary: '#f5222d',
		primaryHover: '#ff4d4f',
		primaryActive: '#cf1322',
		primaryBg: 'rgba(245,34,45,0.08)',
		primaryBgHover: 'rgba(245,34,45,0.14)',
		primaryBorder: 'rgba(245,34,45,0.35)',
		primaryBorderHover: 'rgba(245,34,45,0.6)',
		// Warm primaries → teal success for maximum contrast readability
		success: '#36cfc9', successBg: 'rgba(54,207,201,0.08)', successBorder: 'rgba(54,207,201,0.35)',
		warning: '#fa8c16', warningBg: 'rgba(250,140,22,0.08)', warningBorder: 'rgba(250,140,22,0.35)',
		danger: '#a8071a', dangerBg: 'rgba(168,7,26,0.08)', dangerBorder: 'rgba(168,7,26,0.35)',
		info: '#1890ff', infoBg: 'rgba(24,144,255,0.08)', infoBorder: 'rgba(24,144,255,0.35)',
		tags: { ...STANDARD_TAGS, red: '#a8071a', blue: '#1890ff' },
	},

	crimson: {
		primary: '#a8071a',
		primaryHover: '#cf1322',
		primaryActive: '#820014',
		primaryBg: 'rgba(168,7,26,0.08)',
		primaryBgHover: 'rgba(168,7,26,0.14)',
		primaryBorder: 'rgba(168,7,26,0.35)',
		primaryBorderHover: 'rgba(168,7,26,0.6)',
		success: '#36cfc9', successBg: 'rgba(54,207,201,0.08)', successBorder: 'rgba(54,207,201,0.35)',
		warning: '#fa8c16', warningBg: 'rgba(250,140,22,0.08)', warningBorder: 'rgba(250,140,22,0.35)',
		danger: '#820014', dangerBg: 'rgba(130,0,20,0.08)', dangerBorder: 'rgba(130,0,20,0.35)',
		info: '#1890ff', infoBg: 'rgba(24,144,255,0.08)', infoBorder: 'rgba(24,144,255,0.35)',
		tags: STANDARD_TAGS,
	},

	coral: {
		primary: '#ff7a45',
		primaryHover: '#ff9c6e',
		primaryActive: '#d4380d',
		primaryBg: 'rgba(255,122,69,0.08)',
		primaryBgHover: 'rgba(255,122,69,0.14)',
		primaryBorder: 'rgba(255,122,69,0.35)',
		primaryBorderHover: 'rgba(255,122,69,0.6)',
		success: '#52c41a', successBg: 'rgba(82,196,26,0.08)', successBorder: 'rgba(82,196,26,0.35)',
		warning: '#fa8c16', warningBg: 'rgba(250,140,22,0.08)', warningBorder: 'rgba(250,140,22,0.35)',
		danger: '#cf1322', dangerBg: 'rgba(207,19,34,0.08)', dangerBorder: 'rgba(207,19,34,0.35)',
		info: '#1890ff', infoBg: 'rgba(24,144,255,0.08)', infoBorder: 'rgba(24,144,255,0.35)',
		tags: STANDARD_TAGS,
	},

	orange: {
		primary: '#fa8c16',
		primaryHover: '#ffa940',
		primaryActive: '#d46b08',
		primaryBg: 'rgba(250,140,22,0.08)',
		primaryBgHover: 'rgba(250,140,22,0.14)',
		primaryBorder: 'rgba(250,140,22,0.35)',
		primaryBorderHover: 'rgba(250,140,22,0.6)',
		success: '#52c41a', successBg: 'rgba(82,196,26,0.08)', successBorder: 'rgba(82,196,26,0.35)',
		warning: '#d46b08', warningBg: 'rgba(212,107,8,0.08)', warningBorder: 'rgba(212,107,8,0.35)',
		danger: '#cf1322', dangerBg: 'rgba(207,19,34,0.08)', dangerBorder: 'rgba(207,19,34,0.35)',
		info: '#1890ff', infoBg: 'rgba(24,144,255,0.08)', infoBorder: 'rgba(24,144,255,0.35)',
		tags: STANDARD_TAGS,
	},

	// ── AMBERS / GOLDS ────────────────────────────────────────

	amber: {
		primary: '#faad14',
		primaryHover: '#ffc53d',
		primaryActive: '#d48806',
		primaryBg: 'rgba(250,173,20,0.08)',
		primaryBgHover: 'rgba(250,173,20,0.14)',
		primaryBorder: 'rgba(250,173,20,0.35)',
		primaryBorderHover: 'rgba(250,173,20,0.6)',
		success: '#52c41a', successBg: 'rgba(82,196,26,0.08)', successBorder: 'rgba(82,196,26,0.35)',
		warning: '#d48806', warningBg: 'rgba(212,136,6,0.08)', warningBorder: 'rgba(212,136,6,0.35)',
		danger: '#cf1322', dangerBg: 'rgba(207,19,34,0.08)', dangerBorder: 'rgba(207,19,34,0.35)',
		info: '#1890ff', infoBg: 'rgba(24,144,255,0.08)', infoBorder: 'rgba(24,144,255,0.35)',
		tags: STANDARD_TAGS,
	},

	gold: {
		primary: '#d4b106',
		primaryHover: '#f0d018',
		primaryActive: '#ad8b00',
		primaryBg: 'rgba(212,177,6,0.08)',
		primaryBgHover: 'rgba(212,177,6,0.14)',
		primaryBorder: 'rgba(212,177,6,0.35)',
		primaryBorderHover: 'rgba(212,177,6,0.6)',
		success: '#52c41a', successBg: 'rgba(82,196,26,0.08)', successBorder: 'rgba(82,196,26,0.35)',
		warning: '#ad8b00', warningBg: 'rgba(173,139,0,0.08)', warningBorder: 'rgba(173,139,0,0.35)',
		danger: '#cf1322', dangerBg: 'rgba(207,19,34,0.08)', dangerBorder: 'rgba(207,19,34,0.35)',
		info: '#1890ff', infoBg: 'rgba(24,144,255,0.08)', infoBorder: 'rgba(24,144,255,0.35)',
		tags: STANDARD_TAGS,
	},

	// ── PINKS / ROSES ─────────────────────────────────────────

	pink: {
		primary: '#eb2f96',
		primaryHover: '#f759ab',
		primaryActive: '#c41d7f',
		primaryBg: 'rgba(235,47,150,0.08)',
		primaryBgHover: 'rgba(235,47,150,0.14)',
		primaryBorder: 'rgba(235,47,150,0.35)',
		primaryBorderHover: 'rgba(235,47,150,0.6)',
		success: '#52c41a', successBg: 'rgba(82,196,26,0.08)', successBorder: 'rgba(82,196,26,0.35)',
		warning: '#faad14', warningBg: 'rgba(250,173,20,0.08)', warningBorder: 'rgba(250,173,20,0.35)',
		danger: '#cf1322', dangerBg: 'rgba(207,19,34,0.08)', dangerBorder: 'rgba(207,19,34,0.35)',
		info: '#eb2f96', infoBg: 'rgba(235,47,150,0.08)', infoBorder: 'rgba(235,47,150,0.35)',
		tags: { ...STANDARD_TAGS, blue: '#eb2f96', pink: '#c41d7f' },
	},

	rose: {
		primary: '#f43f5e',
		primaryHover: '#fb7185',
		primaryActive: '#e11d48',
		primaryBg: 'rgba(244,63,94,0.08)',
		primaryBgHover: 'rgba(244,63,94,0.14)',
		primaryBorder: 'rgba(244,63,94,0.35)',
		primaryBorderHover: 'rgba(244,63,94,0.6)',
		success: '#52c41a', successBg: 'rgba(82,196,26,0.08)', successBorder: 'rgba(82,196,26,0.35)',
		warning: '#faad14', warningBg: 'rgba(250,173,20,0.08)', warningBorder: 'rgba(250,173,20,0.35)',
		danger: '#be123c', dangerBg: 'rgba(190,18,60,0.08)', dangerBorder: 'rgba(190,18,60,0.35)',
		info: '#f43f5e', infoBg: 'rgba(244,63,94,0.08)', infoBorder: 'rgba(244,63,94,0.35)',
		tags: { ...STANDARD_TAGS, pink: '#f43f5e', red: '#be123c' },
	},

	babyPink: {
		primary: '#f4a7b9',
		primaryHover: '#f9c0ce',
		primaryActive: '#eb2f96',
		primaryBg: 'rgba(244,167,185,0.08)',
		primaryBgHover: 'rgba(244,167,185,0.14)',
		primaryBorder: 'rgba(244,167,185,0.35)',
		primaryBorderHover: 'rgba(244,167,185,0.6)',
		success: '#52c41a', successBg: 'rgba(82,196,26,0.08)', successBorder: 'rgba(82,196,26,0.35)',
		warning: '#faad14', warningBg: 'rgba(250,173,20,0.08)', warningBorder: 'rgba(250,173,20,0.35)',
		danger: '#ff4d4f', dangerBg: 'rgba(255,77,79,0.08)', dangerBorder: 'rgba(255,77,79,0.35)',
		info: '#eb2f96', infoBg: 'rgba(235,47,150,0.08)', infoBorder: 'rgba(235,47,150,0.35)',
		tags: STANDARD_TAGS,
	},

	// ── NEUTRALS ──────────────────────────────────────────────

	slate: {
		primary: '#475569',
		primaryHover: '#64748b',
		primaryActive: '#334155',
		primaryBg: 'rgba(71,85,105,0.08)',
		primaryBgHover: 'rgba(71,85,105,0.14)',
		primaryBorder: 'rgba(71,85,105,0.35)',
		primaryBorderHover: 'rgba(71,85,105,0.6)',
		success: '#52c41a', successBg: 'rgba(82,196,26,0.08)', successBorder: 'rgba(82,196,26,0.35)',
		warning: '#faad14', warningBg: 'rgba(250,173,20,0.08)', warningBorder: 'rgba(250,173,20,0.35)',
		danger: '#ff4d4f', dangerBg: 'rgba(255,77,79,0.08)', dangerBorder: 'rgba(255,77,79,0.35)',
		info: '#1890ff', infoBg: 'rgba(24,144,255,0.08)', infoBorder: 'rgba(24,144,255,0.35)',
		tags: STANDARD_TAGS,
	},

	charcoal: {
		primary: '#374151',
		primaryHover: '#4b5563',
		primaryActive: '#1f2937',
		primaryBg: 'rgba(55,65,81,0.08)',
		primaryBgHover: 'rgba(55,65,81,0.14)',
		primaryBorder: 'rgba(55,65,81,0.35)',
		primaryBorderHover: 'rgba(55,65,81,0.6)',
		success: '#52c41a', successBg: 'rgba(82,196,26,0.08)', successBorder: 'rgba(82,196,26,0.35)',
		warning: '#faad14', warningBg: 'rgba(250,173,20,0.08)', warningBorder: 'rgba(250,173,20,0.35)',
		danger: '#ff4d4f', dangerBg: 'rgba(255,77,79,0.08)', dangerBorder: 'rgba(255,77,79,0.35)',
		info: '#1890ff', infoBg: 'rgba(24,144,255,0.08)', infoBorder: 'rgba(24,144,255,0.35)',
		tags: STANDARD_TAGS,
	},
};

/** Returns the ColorGroup for the given accent key, falling back to blue. */
export function getColorGroup(accentKey: string): ColorGroup {
	return COLOR_GROUPS[accentKey] ?? COLOR_GROUPS.blue;
}

/** Injects the active color group as CSS custom properties on :root */
export function applyCSSColorGroup(group: ColorGroup): void {
	const root = document.documentElement;
	const entries: [string, string][] = [
		['--mg-primary', group.primary],
		['--mg-primary-hover', group.primaryHover],
		['--mg-primary-active', group.primaryActive],
		['--mg-primary-bg', group.primaryBg],
		['--mg-primary-bg-hover', group.primaryBgHover],
		['--mg-primary-border', group.primaryBorder],
		['--mg-primary-border-hover', group.primaryBorderHover],
		['--mg-success', group.success],
		['--mg-success-bg', group.successBg],
		['--mg-warning', group.warning],
		['--mg-warning-bg', group.warningBg],
		['--mg-danger', group.danger],
		['--mg-danger-bg', group.dangerBg],
		['--mg-info', group.info],
		['--mg-info-bg', group.infoBg],
	];
	entries.forEach(([k, v]) => root.style.setProperty(k, v));
}